import React, { useState, useEffect } from 'react';
import { devLog } from '../lib/log';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function ProtectedRoute({ children, requireRole, requireCoach }) {
  const { user, profile, loading, canCoach } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  /*#__PURE__*/ devLog('🔒 [PROTECTED] ProtectedRoute check:', { loading, user: !!user, profile: !!profile, profileRole: profile?.role, requireRole });

  // Check if there's a Supabase session in localStorage (indicates user was logged in)
  const hasStoredSession = React.useMemo(() => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('supabase') && key.includes('auth')) {
          const value = localStorage.getItem(key);
          if (value && value.includes('access_token')) {
            return true;
          }
        }
      }
    } catch (e) {
      console.error('Error checking stored session:', e);
    }
    return false;
  }, []);

  // Show loading if:
  // 1. Auth is still loading AND we don't have valid user+profile yet
  // 2. We have a user but profile is loading (transient state)
  // 3. We have a stored session but user/profile not yet loaded (page refresh)
  // IMPORTANT: If we already have user AND profile, NEVER show loading
  // This prevents unmounting children during token refresh / background return
  const hasValidAuth = user && profile;
  const shouldShowLoading = !hasValidAuth && (
    loading ||
    (user && !profile) ||
    (hasStoredSession && !user && !profile)
  );

  // Failsafe: if stuck loading for more than 5 seconds, force redirect to login
  useEffect(() => {
    let timeoutId;
    if (shouldShowLoading) {
      setLoadingTimeout(false);
      timeoutId = setTimeout(() => {
        console.warn('🔒 [PROTECTED] Loading timeout reached, forcing navigation');
        setLoadingTimeout(true);
      }, 5000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [shouldShowLoading]);

  // If loading timed out, show session expired message before redirect
  if (loadingTimeout && !user && !profile) {
    /*#__PURE__*/ devLog('🔒 [PROTECTED] Loading timeout - showing session expired');
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f7fa',
        padding: '20px',
        textAlign: 'center',
        gap: '16px'
      }}>
        <div style={{ fontSize: '48px' }}>🔒</div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827' }}>
          Sessione scaduta
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280', maxWidth: '300px' }}>
          Per sicurezza, devi effettuare nuovamente l'accesso.
        </div>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            marginTop: '8px',
            padding: '12px 32px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Accedi
        </button>
      </div>
    );
  }

  if (shouldShowLoading && !loadingTimeout) {
    /*#__PURE__*/ devLog('🔒 [PROTECTED] Loading state - waiting for auth...', { loading, hasUser: !!user, hasProfile: !!profile, hasStoredSession });
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f7fa'
      }}>
        <div style={{
          fontSize: '18px',
          color: '#667eea',
          fontWeight: 600
        }}>
          Caricamento...
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    /*#__PURE__*/ devLog('🔒 [PROTECTED] No user or profile, redirecting to login');
    return <Navigate to="/" replace />;
  }

  // PB-002: gate legale SOLO per utenti entrati con un provider social (es.
  // Google) e senza consenso Privacy/Termini registrato. Gli utenti
  // email/password non vengono mai toccati (inclusi i legacy con consenso NULL).
  const isSocialLogin = (user.app_metadata?.provider && user.app_metadata.provider !== 'email')
    || (Array.isArray(user.app_metadata?.providers) && user.app_metadata.providers.some(p => p !== 'email'));
  if (isSocialLogin && !profile.terms_accepted_at) {
    /*#__PURE__*/ devLog('🔒 [PROTECTED] Social login senza consenso → /consenso');
    return <Navigate to="/consenso" replace />;
  }

  // Area coach: richiede la capability (Modalità Coach attiva, o coach/admin)
  if (requireCoach && !canCoach) {
    return <Navigate to="/athlete" replace />;
  }

  // requireRole rigido: solo per aree veramente riservate (es. admin)
  if (requireRole && profile.role !== requireRole) {
    const redirectPath = profile.role === 'admin' ? '/admin' : '/athlete';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

export default ProtectedRoute;
